import { useEffect, useMemo, useState } from "react";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link2, LockKeyhole, Target } from "lucide-react";
import { z } from "zod";
import type { ApiError } from "@/lib/api";
import { errorMessage } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { useMatches } from "@/features/matches/useMatches";
import type {
  GoalContext,
  GoalPriority,
  GoalStatus,
  GoalVisibility,
  PlayerGoal,
} from "@/lib/types";
import type { CoachGoalMutations } from "../hooks/useCoachGoals";
import { useRoster } from "../hooks/useRoster";
import { useTeamSettings } from "../hooks/useTeamSettings";
import { CoachModal } from "./CoachModal";
import { ReferenceVideoField } from "@/features/coach-review/components/ReferenceVideoField";
import { goalVisibilitySegmentOptions } from "../visibilityVocabulary";

const CONTEXT_OPTIONS: Array<{ value: GoalContext; label: string }> = [
  { value: "next_match", label: "Next match" },
  { value: "next_training", label: "Next training" },
  { value: "season_goal", label: "Season goal" },
  { value: "other", label: "Other" },
];
// Same wording as the note ladder — "Coaching staff only" was a
// third phrasing for a concept the coach already learned twice.
const VISIBILITY_OPTIONS = goalVisibilitySegmentOptions();
const PRIORITY_OPTIONS: Array<{ value: GoalPriority; label: string }> = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];
const STATUS_OPTIONS: Array<{ value: GoalStatus; label: string }> = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "needs_follow_up", label: "Needs follow-up" },
  { value: "achieved", label: "Achieved" },
  { value: "archived", label: "Archived" },
];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const GOAL_FORM_SCHEMA = z.object({
  playerId: z
    .string()
    .min(1, "Pick a player for this goal.")
    .max(120, "Player identifier is too long."),
  context: z.enum(["next_match", "next_training", "season_goal", "other"]),
  visibility: z.enum(["player", "coach"]),
  // Reference video (#675). camelCase here to match this form's
  // convention; mapped to the snake_case API fields at the boundary below.
  refVideoProvider: z.enum(["", "youtube", "vimeo", "file"]),
  refVideoId: z.string().max(2048),
  refVideoLabel: z.string().max(200),
  refVideoTitle: z.string().max(300),
  refVideoThumb: z.string().max(500),
  priority: z.enum(["low", "medium", "high"]),
  status: z.enum([
    "open",
    "in_progress",
    "needs_follow_up",
    "achieved",
    "archived",
  ]),
  title: z
    .string()
    .max(160, "Goal title must be 160 characters or fewer.")
    .refine((value) => value.trim().length > 0, {
      message: "Goal title is required.",
    }),
  description: z
    .string()
    .max(2000, "Action plan must be 2,000 characters or fewer."),
  targetDate: z
    .string()
    .max(10, "Target date must be YYYY-MM-DD or left blank.")
    .refine((value) => !value || DATE_RE.test(value), {
      message: "Target date must be YYYY-MM-DD or left blank.",
    }),
  targetMatchId: z
    .string()
    .max(120, "Target match identifier is too long."),
  successCriteria: z
    .string()
    .max(2000, "Success criteria must be 2,000 characters or fewer."),
  coachPrivateNote: z
    .string()
    .max(2000, "Coach private note must be 2,000 characters or fewer."),
});

type GoalFormValues = z.infer<typeof GOAL_FORM_SCHEMA>;

/** Source evidence carried into a new goal from a feedback item. */
export type GoalComposerSource = {
  source_note_id?: number;
  source_clip_id?: number;
  source_playlist_id?: number;
  seedTitle?: string;
  seedDescription?: string;
};

export type GoalComposerModalProps = {
  /** `null` creates a goal; a goal edits it. */
  goal: PlayerGoal | null;
  preselectedPlayerId?: string;
  source?: GoalComposerSource;
  mutations: CoachGoalMutations;
  onClose: () => void;
  onSaved?: () => void;
  returnFocusTo?: HTMLElement | null;
};

export function GoalComposerModal({
  goal,
  preselectedPlayerId,
  source,
  mutations,
  onClose,
  onSaved,
  returnFocusTo,
}: GoalComposerModalProps) {
  const isEdit = goal !== null;
  const toast = useToast();
  const rosterQuery = useRoster();
  // The team's own "Goal visibility" setting — stored and served but
  // never read until now, so setting it changed nothing. It seeds a
  // NEW goal only; editing keeps the goal's own visibility.
  const teamSettingsQuery = useTeamSettings();
  const defaultGoalVisibility =
    teamSettingsQuery.data?.settings?.["goals.default_visibility"];
  const matchesQuery = useMatches();
  const players = rosterQuery.data?.players ?? [];
  const matches = matchesQuery.data ?? [];
  const [apiError, setApiError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const form = useForm<GoalFormValues>({
    resolver: zodResolver(GOAL_FORM_SCHEMA),
    defaultValues: {
      playerId: goal?.player_id ?? preselectedPlayerId ?? "",
      context: goal?.context ?? "next_match",
      visibility: goal?.visibility ?? "player",
      priority: goal?.priority ?? "medium",
      status: goal?.status ?? "open",
      title: goal ? (goal.title ?? "") : (source?.seedTitle ?? ""),
      description: goal
        ? (goal.description ?? "")
        : (source?.seedDescription ?? ""),
      targetDate: goal?.target_date ?? "",
      targetMatchId: goal?.target_match_id ?? "",
      successCriteria: goal?.success_criteria ?? "",
      coachPrivateNote: goal?.coach_private_note ?? "",
      refVideoProvider: goal?.ref_video_provider ?? "",
      refVideoId: goal?.ref_video_id ?? "",
      refVideoLabel: goal?.ref_video_label ?? "",
      refVideoTitle: goal?.ref_video_title ?? "",
      refVideoThumb: goal?.ref_video_thumb ?? "",
    },
  });

  const {
    control,
    formState: { errors, isDirty, isSubmitting },
    getValues,
    register,
    setValue,
    watch,
  } = form;

  // The normal entry points preselect a player. Keep the standalone fallback
  // for callers that open before the roster query has resolved, without
  // marking the untouched form dirty.
  useEffect(() => {
    if (getValues("playerId") || !players[0]) return;
    setValue("playerId", players[0].id, { shouldDirty: false });
  }, [getValues, players, setValue]);

  // Same shape as the player fallback above: `useTeamSettings` is an
  // async query with no `initialData` and this modal mounts on a click,
  // so the setting is almost always still undefined when `useForm`
  // captures its `defaultValues`. Reading it there silently fell
  // through to the hardcoded "player" and never corrected itself — the
  // setting only appeared to work when the query was warm in cache.
  //
  // Seeding a NEW goal only: `isEdit` keeps the goal's own visibility,
  // and `dirtyFields.visibility` means a coach who already picked one
  // is never overridden. `shouldDirty: false` so seeding a default
  // doesn't make an untouched form look edited (the discard prompt
  // keys off `isDirty`).
  useEffect(() => {
    if (isEdit || !defaultGoalVisibility) return;
    if (form.formState.dirtyFields.visibility) return;
    setValue("visibility", defaultGoalVisibility, { shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultGoalVisibility, isEdit, setValue]);

  const matchLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const match of matches) {
      const teams = `${match.home_team} vs ${match.away_team}`;
      map.set(match.id, match.date ? `${teams} · ${match.date}` : teams);
    }
    return map;
  }, [matches]);

  const sourceSummary = useMemo(
    () => goalSourceSummary(goal, source),
    [goal, source],
  );

  const requestClose = () => {
    if (isSubmitting || mutations.isPending) return;
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    setApiError(null);
    try {
      const shared = {
        title: values.title.trim(),
        description: values.description.trim(),
        context: values.context,
        visibility: values.visibility,
        priority: values.priority,
        status: values.status,
        target_date: values.targetDate,
        success_criteria: values.successCriteria.trim(),
        coach_private_note: values.coachPrivateNote.trim(),
        target_match_id: values.targetMatchId || null,
        // Empty string -> null so clearing actually clears the columns.
        ref_video_provider: values.refVideoProvider || null,
        ref_video_id: values.refVideoId || null,
        ref_video_label: values.refVideoLabel || null,
        ref_video_title: values.refVideoTitle || null,
        ref_video_thumb: values.refVideoThumb || null,
      };

      if (isEdit) {
        await mutations.updateGoal(goal.id, shared);
      } else {
        await mutations.createGoal({
          player_id: values.playerId,
          ...shared,
          ...(source?.source_note_id != null && {
            source_note_id: source.source_note_id,
          }),
          ...(source?.source_clip_id != null && {
            source_clip_id: source.source_clip_id,
          }),
          ...(source?.source_playlist_id != null && {
            source_playlist_id: source.source_playlist_id,
          }),
        });
      }

      toast.success(isEdit ? "Goal updated" : "Goal created");
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setApiError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : `Could not ${isEdit ? "update" : "create"} the goal.`,
      );
      toast.error(`Couldn't ${isEdit ? "update" : "create"} goal`, {
        description: errorMessage(err),
      });
    }
  });

  const firstError =
    errors.title?.message ??
    errors.playerId?.message ??
    errors.targetDate?.message ??
    errors.description?.message ??
    errors.successCriteria?.message ??
    errors.coachPrivateNote?.message ??
    errors.targetMatchId?.message ??
    apiError;

  return (
    <>
      {confirmDiscard ? (
        <ConfirmDialog
          title="Discard goal changes?"
          message="Your unsaved goal changes will be lost."
          confirmLabel="Discard"
          danger
          onConfirm={onClose}
          onClose={() => setConfirmDiscard(false)}
          data-testid="goal-composer-discard-confirm"
        />
      ) : null}

      <CoachModal
        title={isEdit ? "Edit player goal" : "New player goal"}
        description="Turn coaching context into one clear focus, a practical action, and a measurable finish line."
        size="wide"
        onClose={requestClose}
        returnFocusTo={returnFocusTo}
        data-testid="goal-composer-modal"
        bodyClassName="bg-canvas"
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p
              className="min-h-5 text-xs text-destructive sm:flex-1"
              aria-live="polite"
              data-testid={firstError ? "goal-composer-error" : undefined}
            >
              {firstError ?? ""}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={requestClose}
                disabled={isSubmitting || mutations.isPending}
                data-testid="goal-composer-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="goal-composer-form"
                disabled={isSubmitting || mutations.isPending}
                data-testid="goal-composer-submit"
              >
                {isSubmitting || mutations.isPending
                  ? "Saving…"
                  : isEdit
                    ? "Save goal"
                    : "Create goal"}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="goal-composer-form"
          className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(19rem,0.85fr)] lg:items-start"
          onSubmit={handleSubmit}
          noValidate
        >
          <section
            className="min-w-0 rounded-panel border border-rule bg-surface"
            aria-labelledby="goal-writing-heading"
            data-testid="goal-composer-writing"
          >
            <SectionHeading
              id="goal-writing-heading"
              icon={<Target aria-hidden="true" className="size-4" />}
              title="The development focus"
              description="Write this so the player knows what to notice, try, and repeat."
            />

            <div className="flex flex-col gap-4 p-4 sm:p-5">
              {sourceSummary ? (
                <div
                  className="rounded-inset border border-rule bg-elevated px-3 py-2.5"
                  data-testid="goal-composer-source"
                >
                  <div className="flex items-start gap-2">
                    <Link2
                      aria-hidden="true"
                      className="mt-0.5 size-4 shrink-0 text-accent"
                    />
                    <div className="min-w-0">
                      <p className="text-[0.65rem] font-display uppercase tracking-wider text-ink-muted">
                        {sourceSummary.label}
                      </p>
                      <p className="truncate text-sm font-display text-ink">
                        {sourceSummary.text}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <Field label="Goal title" error={errors.title?.message}>
                <input
                  type="text"
                  {...register("title")}
                  data-testid="goal-composer-title"
                  autoFocus
                  aria-invalid={!!errors.title}
                  placeholder="Scan before receiving"
                  className={INPUT_CLASS}
                />
              </Field>

              <Field
                label="Action plan"
                helper="Give the player a repeatable action, not just an outcome."
                error={errors.description?.message}
              >
                <textarea
                  {...register("description")}
                  data-testid="goal-composer-description"
                  rows={6}
                  placeholder="What should the player try next?"
                  className={INPUT_CLASS}
                />
              </Field>

              <Field
                label="Success criteria"
                helper="Describe the evidence that tells you both this is working."
                error={errors.successCriteria?.message}
              >
                <textarea
                  {...register("successCriteria")}
                  data-testid="goal-composer-success-criteria"
                  rows={4}
                  placeholder="How will we know this goal is working?"
                  className={INPUT_CLASS}
                />
              </Field>
            </div>
          </section>

          <section
            className="min-w-0 rounded-panel border border-rule bg-surface"
            aria-labelledby="goal-tracking-heading"
            data-testid="goal-composer-tracking"
          >
            <SectionHeading
              id="goal-tracking-heading"
              title="Delivery & tracking"
              description="Choose who receives it and when the coach should revisit it."
            />

            <div className="grid min-w-0 gap-4 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-1">
              <Field label="Player" error={errors.playerId?.message}>
                <Controller
                  control={control}
                  name="playerId"
                  render={({ field }) => (
                    <TkticSelect
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isEdit || rosterQuery.isLoading}
                      data-testid="goal-composer-player"
                      ariaLabel="Player"
                      options={
                        players.length === 0
                          ? [{ value: "", label: "No players yet" }]
                          : players.map((player) => ({
                              value: player.id,
                              label: player.jersey_number
                                ? `#${player.jersey_number} ${player.display_name}`
                                : player.display_name,
                            }))
                      }
                    />
                  )}
                />
              </Field>

              <Field label="Context">
                <Controller
                  control={control}
                  name="context"
                  render={({ field }) => (
                    <TkticSelect
                      value={field.value}
                      onChange={(value) => field.onChange(value as GoalContext)}
                      data-testid="goal-composer-context"
                      ariaLabel="Context"
                      options={CONTEXT_OPTIONS}
                    />
                  )}
                />
              </Field>

              <Field label="Visibility">
                <Controller
                  control={control}
                  name="visibility"
                  render={({ field }) => (
                    <SegmentedControl
                      value={field.value}
                      onChange={(value) => field.onChange(value as GoalVisibility)}
                      data-testid="goal-composer-visibility"
                      ariaLabel="Visibility"
                      options={VISIBILITY_OPTIONS}
                    />
                  )}
                />
              </Field>

              {/* Nested inside the ~19rem rail, a hard 2-col split leaves each
                * control ~145px. Collapse at `lg`, where the rail becomes a
                * single narrow column, so both get its full width. */}
              <div className="grid grid-cols-2 gap-3 sm:col-span-2 lg:grid-cols-1 lg:col-span-1">
                <Field label="Priority">
                  <Controller
                    control={control}
                    name="priority"
                    render={({ field }) => (
                      <TkticSelect
                        value={field.value}
                        onChange={(value) =>
                          field.onChange(value as GoalPriority)
                        }
                        data-testid="goal-composer-priority"
                        ariaLabel="Priority"
                        options={PRIORITY_OPTIONS}
                      />
                    )}
                  />
                </Field>
                <Field label="Status">
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <TkticSelect
                        value={field.value}
                        onChange={(value) => field.onChange(value as GoalStatus)}
                        data-testid="goal-composer-status"
                        ariaLabel="Status"
                        options={STATUS_OPTIONS}
                      />
                    )}
                  />
                </Field>
              </div>

              <Field
                label="Target date"
                helper="Leave blank if this stays open-ended."
                error={errors.targetDate?.message}
              >
                <Controller
                  control={control}
                  name="targetDate"
                  render={({ field }) => (
                    <TkticDateInput
                      value={field.value}
                      onChange={field.onChange}
                      data-testid="goal-composer-target-date"
                    />
                  )}
                />
              </Field>

              <Field label="Target match" error={errors.targetMatchId?.message}>
                <Controller
                  control={control}
                  name="targetMatchId"
                  render={({ field }) => (
                    <TkticSelect
                      value={field.value}
                      onChange={field.onChange}
                      data-testid="goal-composer-target-match"
                      ariaLabel="Target match"
                      options={[
                        { value: "", label: "No target match" },
                        ...matches.map((match) => ({
                          value: match.id,
                          label: matchLabelById.get(match.id) ?? match.id,
                        })),
                      ]}
                    />
                  )}
                />
              </Field>

              {/* Player-VISIBLE, so it sits above the coach-only block
                * below rather than beside it. Collapsed when empty (#675). */}
              <div className="sm:col-span-2">
                <ReferenceVideoField
                  values={{
                    ref_video_provider: watch("refVideoProvider"),
                    ref_video_id: watch("refVideoId"),
                    ref_video_label: watch("refVideoLabel"),
                    ref_video_title: watch("refVideoTitle"),
                    ref_video_thumb: watch("refVideoThumb"),
                  }}
                  onChange={(patch) => {
                    const map = {
                      ref_video_provider: "refVideoProvider",
                      ref_video_id: "refVideoId",
                      ref_video_label: "refVideoLabel",
                      ref_video_title: "refVideoTitle",
                      ref_video_thumb: "refVideoThumb",
                    } as const;
                    for (const [snake, camel] of Object.entries(map)) {
                      if (snake in patch) {
                        setValue(
                          camel,
                          patch[snake as keyof typeof patch] as never,
                          { shouldDirty: true },
                        );
                      }
                    }
                  }}
                />
              </div>

              <div className="border-t border-rule pt-4 sm:col-span-2 lg:col-span-1">
                <Field
                  label="Coach private note"
                  helper="Private coaching context. Never shown to the player or family."
                  error={errors.coachPrivateNote?.message}
                >
                  <div className="mb-2 flex items-center gap-2 text-xs text-ink-muted">
                    <LockKeyhole aria-hidden="true" className="size-4" />
                    Coach-only
                  </div>
                  <textarea
                    {...register("coachPrivateNote")}
                    data-testid="goal-composer-private-note"
                    rows={4}
                    placeholder="Internal coaching context"
                    className={INPUT_CLASS}
                  />
                </Field>
              </div>
            </div>
          </section>
        </form>
      </CoachModal>
    </>
  );
}

const INPUT_CLASS =
  "w-full min-w-0 rounded-control border border-rule bg-field px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

function SectionHeading({
  id,
  icon,
  title,
  description,
}: {
  id: string;
  icon?: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <header className="border-b border-rule bg-elevated px-4 py-3 sm:px-5">
      <div className="flex items-center gap-2">
        {icon}
        <h3 id={id} className="font-display text-base text-ink">
          {title}
        </h3>
      </div>
      <p className="mt-1 text-xs text-ink-muted">{description}</p>
    </header>
  );
}

function Field({
  label,
  helper,
  error,
  children,
}: {
  label: string;
  helper?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      {children}
      {error ? (
        <span className="text-xs text-destructive">{error}</span>
      ) : helper ? (
        <span className="text-xs text-ink-muted">{helper}</span>
      ) : null}
    </label>
  );
}

function goalSourceSummary(
  goal: PlayerGoal | null,
  source?: GoalComposerSource,
): { label: string; text: string } | null {
  if (goal?.source_note) {
    return {
      label: "Source note",
      text:
        goal.source_note.title?.trim() ||
        goal.source_note.event_title?.trim() ||
        `Note #${goal.source_note.id}`,
    };
  }
  if (goal?.source_clip) {
    return {
      label: "Source clip",
      text: goal.source_clip.title?.trim() || `Clip #${goal.source_clip.id}`,
    };
  }
  if (goal?.source_playlist) {
    return {
      label: "Source playlist",
      text:
        goal.source_playlist.title?.trim() ||
        `Playlist #${goal.source_playlist.id}`,
    };
  }
  if (!source) return null;

  const kind = source.source_note_id
    ? "Source note"
    : source.source_clip_id
      ? "Source clip"
      : source.source_playlist_id
        ? "Source playlist"
        : null;
  if (!kind) return null;

  return {
    label: kind,
    text: source.seedTitle?.trim() || "Linked coaching evidence",
  };
}
